<!-- Responsibility: Implement save slot card behavior for the ui/components/lobby surface.
Scope: Owns ui/components/lobby concerns only; cross-surface orchestration is excluded. -->
<save-slot-card>
  <!-- props: save ({ id, state: GameState }), onSelect (id => void), onDelete (id => void) -->
  <div class="save-slot { state.confirming ? 'confirming' : '' } { isLoading ? 'loading' : '' }" aria-busy={ isLoading ? 'true' : 'false' }>
    <div if={ isLoading } class="slot-main slot-skeleton loading-skeleton" aria-hidden="true">
      <header>
        <span class="loading-line long"></span>
        <span class="loading-chip"></span>
      </header>
      <div class="slot-preview-band loading-grid">
        <span class="loading-line full"></span>
        <span class="loading-line mid"></span>
        <div class="slot-signal-row">
          <span class="loading-chip"></span>
          <span class="loading-chip" style="width: 5.75rem;"></span>
        </div>
      </div>
      <footer>
        <span class="loading-line short"></span>
        <span class="loading-line mid"></span>
      </footer>
    </div>

    <div
      if={ !isLoading }
      class="slot-main tooltip-wrap"
      role="button"
      tabindex={ !state.confirming && !isLoading ? '0' : '-1' }
      aria-label={ !state.confirming && !isLoading ? `Open save ${label}` : `Save ${label}` }
      onclick={ !state.confirming && !isLoading ? select : null }
      onkeydown={ onSelectKeydown }
    >
      <header>
        <span class="title">{ label }</span>
        <span class="origin { originClass }">{ originTag }</span>
      </header>
      <div class="slot-preview-band">
        <p if={ locationStr } class="slot-location-line">{ locationStr }</p>
        <div class="slot-signal-row">
          <span if={ dayStr } class="slot-signal">Timeline · { dayStr }</span>
          <span if={ dateStr } class="slot-signal">Save point recorded</span>
        </div>
      </div>
      <footer>
        <span>{ dayStr }</span>
        <span>{ dateStr }</span>
      </footer>
      <div class="tooltip" data-position="bottom" aria-hidden="true" data-tip-custom="true">
        <span><b>Reality</b><span>{ label }</span></span>
        <span><b>Origin</b><span>{ originTag.replace(/[\[\]]/g, '') }</span></span>
        <span if={ dayStr }><b>Timeline</b><span>{ dayStr }</span></span>
        <span if={ locationStr }><b>Location</b><span>{ locationStr }</span></span>
        <span if={ dateStr }><b>Saved</b><span>{ dateStr }</span></span>
      </div>
    </div>

    <div if={ !state.confirming && !isLoading } class="slot-actions">
      <button class="del-btn tooltip-wrap" onclick={ startDelete } aria-label="Delete save">
        🗑
        <div class="tooltip" data-position="top" data-tip-custom="true">Delete save</div>
      </button>
    </div>

    <div if={ state.confirming } class="slot-confirm">
      <span class="confirm-label">ERASE REALITY?</span>
      <button class="confirm-yes" onclick={ confirmDelete }>YES</button>
      <button class="confirm-no"  onclick={ cancelDelete }>NO</button>
    </div>
  </div>

  <script>
    import { formatDay } from '../../../engine/temporal.js'

    const ORIGIN_LABELS = {
      local:               '[ORIGINAL]',
      imported_blueprint:  '[IMPORTED WORLD]',
      imported_scenario:   '[IMPORTED GAME]',
      imported_vessel:     '[IMPORTED VESSEL]',
      imported_starter:    '[IMPORTED STARTER]',
    }

    const ORIGIN_CLASSES = {
      local:               'local',
      imported_blueprint:  'world',
      imported_scenario:   'scenario',
      imported_vessel:     'vessel',
      imported_starter:    'starter',
    }

    export default {
      state: { confirming: false },

      get label() {
        const s = this.props.save?.state
        return s?.meta?.name || s?.player?.identity || 'Unknown'
      },

      get originTag() {
        const origin = this.props.save?.state?.meta?.origin ?? 'local'
        return ORIGIN_LABELS[origin] ?? ORIGIN_LABELS.local
      },

      get originClass() {
        const origin = this.props.save?.state?.meta?.origin ?? 'local'
        return ORIGIN_CLASSES[origin] ?? ORIGIN_CLASSES.local
      },

      get dayStr() {
        const clock = this.props.save?.state?.world?.clock
        return clock ? formatDay(clock) : ''
      },

      get locationStr() {
        return this.props.save?.state?.currentRoom?.name || ''
      },

      get dateStr() {
        const ts = this.props.save?.state?.meta?.timestamp
        return ts ? new Date(ts).toLocaleString() : ''
      },

      get isLoading() {
        return !!this.props.loading
      },

      select() {
        this.props.onSelect?.(this.props.save?.id)
      },

      onSelectKeydown(e) {
        if (this.state.confirming || this.isLoading) return
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        this.select()
      },

      startDelete(e) {
        e.stopPropagation()
        this.update({ confirming: true })
      },

      confirmDelete(e) {
        e.stopPropagation()
        this.props.onDelete?.(this.props.save?.id)
        this.update({ confirming: false })
      },

      cancelDelete(e) {
        e.stopPropagation()
        this.update({ confirming: false })
      },
    }
  </script>

  <style>
    .save-slot {
      display: flex;
      align-items: stretch;
      border: 1px solid var(--border, #1e2a3a);
      border-radius: 0.875rem;
      overflow: visible;
      position: relative;
      background: linear-gradient(135deg, color-mix(in srgb, var(--surface) var(--alpha-high), var(--bg)), color-mix(in srgb, var(--surface-card-1) var(--alpha-high), #03060b));
      animation: slot-stage-in 0.4s cubic-bezier(0.22, 0.9, 0.22, 1.12) both;
      transition: border-color 0.2s, transform 0.2s ease, box-shadow 0.2s ease;
    }

    .save-slot::before,
    .save-slot::after {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      transition: opacity 0.22s ease, transform 0.22s ease;
    }

    .save-slot::before {
      background:
        radial-gradient(circle at var(--alpha-glow) var(--alpha-low), color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-glow), transparent), transparent var(--alpha-subtle)),
        linear-gradient(180deg, color-mix(in srgb, var(--text-prose) var(--alpha-ghost), transparent), transparent var(--alpha-low));
      opacity: 0.5;
    }

    .save-slot::after {
      inset: auto -var(--alpha-low) var(--alpha-mid) var(--alpha-low);
      height: var(--alpha-mid);
      background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-low), transparent), transparent);
      opacity: 0;
      transform: translateX(-var(--alpha-low));
    }

    .save-slot:hover {
      border-color: var(--gold, var(--gold));
      transform: perspective(75rem) translateY(-0.1875rem) rotateX(3deg) scale(1.01);
      box-shadow: 0 1.125rem 1.75rem rgba(0, 0, 0, 0.2), 0 0 0 1px color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-low), transparent);
    }
    .save-slot:hover::before,
    .save-slot:focus-within::before {
      opacity: 0.74;
    }
    .save-slot:hover::after,
    .save-slot:focus-within::after {
      opacity: 1;
      transform: translateX(0);
    }
    .save-slot.confirming { border-color: var(--danger, var(--danger)); }
    .save-slot.loading {
      border-color: color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-ghost), var(--border));
      cursor: progress;
      pointer-events: none;
    }

    .slot-main {
      flex: 1;
      padding: 0.85rem var(--space-md);
      cursor: pointer;
      position: relative;
    }

    .slot-skeleton {
      cursor: progress;
      display: grid;
      gap: var(--space-sm);
      pointer-events: none;
    }
    .slot-main header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--space-xs);
      margin-bottom: var(--space-xs);
    }
    .slot-main footer {
      display: flex;
      justify-content: space-between;
      gap: 0.6rem;
      font-size: var(--space-sm);
      color: var(--text-muted);
    }
    .title { font-weight: bold; font-size: var(--space-md); color: var(--gold, var(--gold)); letter-spacing: 0.02em; }
    .slot-preview-band {
      display: grid;
      gap: var(--space-xs);
      padding: 0.62rem var(--space-sm);
      margin: 0 0 var(--space-xs);
      border-radius: 0.9rem;
      border: 1px solid color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-low), transparent);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-card-2) var(--alpha-high), #09111d), color-mix(in srgb, var(--surface-card-3) var(--alpha-ghost), #03060b)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-subtle), transparent), transparent var(--alpha-mid));
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.04),
        0 0.75rem 1.5rem rgba(0, 0, 0, 0.12);
    }
    .slot-location-line {
      color: var(--text-muted);
      font-size: 0.84rem;
      line-height: 1.4;
      margin: 0;
    }
    .slot-signal-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs) var(--space-xs);
    }
    .slot-signal {
      display: inline-flex;
      align-items: center;
      min-height: 2em;
      padding: 0.32em 0.64em;
      border-radius: 62.4375rem;
      border: 1px solid color-mix(in srgb, var(--border, #1e2a3a) var(--alpha-high), #41536c);
      background: color-mix(in srgb, var(--surface-card-3) var(--alpha-high), var(--bg));
      color: var(--text-muted);
      font-size: var(--space-sm);
      letter-spacing: 0.04em;
    }
    .origin {
      font-size: var(--space-sm);
      letter-spacing: 0.08em;
      color: var(--text-muted);
      white-space: nowrap;
    }
    .origin.local    { color: var(--text-muted); }
    .origin.world    { color: var(--brand-primary, var(--brand-primary)); }
    .origin.scenario { color: var(--gold, var(--gold)); }
    .origin.vessel   { color: var(--sem-success-fg); }
    .origin.starter  { color: #f97316; }
    .slot-loading {
      color: var(--gold, var(--gold));
      letter-spacing: 0.06em;
      font-weight: bold;
      animation: slot-loading-pulse 0.9s ease-in-out infinite;
    }



    .slot-actions {
      display: flex;
      align-items: center;
      padding: 0 0.65rem;
      border-left: 1px solid var(--border, #1e2a3a);
    }
    .del-btn {
      background: none;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
      font-size: var(--space-md);
      padding: var(--space-xs) var(--space-xs);
      border-radius: 0.1875rem;
      transition: color 0.15s, background 0.15s;
    }
    .del-btn:hover { color: var(--danger, var(--danger)); background: color-mix(in srgb, var(--danger) var(--alpha-glow), transparent); }

    .slot-confirm {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
      padding: 0 var(--space-sm);
      background: color-mix(in srgb, var(--danger, var(--danger)) var(--alpha-subtle), transparent);
    }
    .confirm-label {
      font-size: 0.65rem;
      color: var(--danger, var(--danger));
      letter-spacing: 0.06em;
      white-space: nowrap;
    }
    .confirm-yes, .confirm-no {
      background: none;
      border: 1px solid currentColor;
      border-radius: 0.1875rem;
      cursor: pointer;
      font-size: 0.65rem;
      padding: var(--space-xs) var(--space-xs);
      font-family: inherit;
      letter-spacing: 0.05em;
    }
    .confirm-yes { color: var(--danger, var(--danger)); }
    .confirm-yes:hover { background: var(--danger, var(--danger)); color: var(--text-prose); }
    .confirm-no  { color: var(--text-muted); }
    .confirm-no:hover  { background: var(--border, #1e2a3a); }

    .slot-hovercard {
      position: absolute;
      top: var(--space-sm);
      right: var(--space-sm);
      width: min(18rem, calc(100% - 1.4rem));
      padding: var(--space-sm) var(--space-sm);
      display: grid;
      gap: var(--space-xs);
      border-radius: 0.75rem;
      border: 1px solid color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-low), transparent);
      background: linear-gradient(180deg, rgba(7, 10, 16, 0.96), rgba(4, 6, 10, 0.98));
      box-shadow: 0 1rem 1.875rem rgba(0, 0, 0, 0.3);
      opacity: 0;
      transform: translateY(0.25rem);
      transition: opacity 0.18s ease, transform 0.18s ease;
      pointer-events: none;
    }

    .save-slot:hover .slot-hovercard,
    .save-slot:focus-within .slot-hovercard {
      opacity: 1;
      transform: translateY(0);
    }

    .save-slot:hover .slot-signal,
    .save-slot:focus-within .slot-signal {
      border-color: color-mix(in srgb, var(--gold, var(--gold)) var(--alpha-subtle), transparent);
      color: var(--text-muted);
    }

    .slot-hovercard > span {
      display: grid;
      gap: 0.15rem;
    }

    .slot-hovercard > span > b {
      color: var(--gold, var(--gold));
      font-size: var(--type-3xs);
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .slot-hovercard > span > span {
      color: var(--text-prose, #e5e7eb);
      font-size: var(--space-sm);
      line-height: 1.4;
    }

    @media (max-width: 45rem) {
      .slot-hovercard {
        display: none;
      }

      .title {
        font-size: var(--space-md);
      }

      .slot-location-line,
      .slot-main footer {
        font-size: var(--space-sm);
      }
    }
  </style>
</save-slot-card>
